import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, ArrowRight, ArrowLeft, Clock, Check, ShieldCheck, Loader2, Sparkles,
} from "lucide-react";

const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/post/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-display font-bold">Article not found</h1>
        <Link to="/blog" className="text-primary underline">Browse all guides</Link>
      </div>
    );
  }
  if (!post) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const canonical = typeof window !== "undefined" ? window.location.href : "";
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description,
    image: post.banner_image_url,
    author: { "@type": "Organization", name: "AutoGridSocial" },
    publisher: { "@type": "Organization", name: "AutoGridSocial" },
    dateModified: post.updated_at,
    datePublished: post.created_at || post.updated_at,
    mainEntityOfPage: canonical,
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (post.faq_items || []).map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="blog-post">
      <Helmet>
        <title>{`${post.title} | AutoGridSocial`}</title>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:image" content={post.banner_image_url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
            <span className="font-display font-bold text-lg tracking-tight">AutoGridSocial</span>
          </Link>
          <Button data-testid="blog-post-nav-cta" onClick={handleLogin} className="bg-primary hover:bg-primary/90 text-white text-sm">Get started</Button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="blog-back">
          <ArrowLeft className="h-4 w-4" /> All guides
        </Link>
        <Badge variant="outline" className="border-border text-muted-foreground mb-4">{post.audience}</Badge>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-4">{post.title}</h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.read_minutes} min read</span>
        </div>
        {post.banner_image_url && (
          <div className="rounded-2xl overflow-hidden border border-border mb-8">
            <img src={post.banner_image_url} alt={post.audience} className="w-full h-64 object-cover" data-testid="blog-post-banner" />
          </div>
        )}

        <p className="text-lg text-foreground/90 leading-relaxed mb-10">{post.intro}</p>

        {post.key_takeaways?.length > 0 && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-6 mb-10" data-testid="blog-takeaways">
            <h2 className="font-display font-semibold flex items-center gap-2 mb-4"><Sparkles className="h-4 w-4 text-primary" /> Key takeaways</h2>
            <ul className="space-y-2.5">
              {post.key_takeaways.map((k, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {k}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-10">
          {(post.sections || []).map((s, i) => (
            <section key={i} data-testid={`blog-section-${i}`}>
              <h2 className="font-display text-2xl font-bold tracking-tight mb-3">{s.heading}</h2>
              <p className="text-base text-muted-foreground leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>

        {/* Related landing CTA */}
        <div className="rounded-2xl border border-border bg-card p-8 my-12 text-center" data-testid="blog-related-cta">
          <h3 className="font-display text-2xl font-bold tracking-tight mb-3">
            Built for {post.related_landing_label}
          </h3>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            See exactly how AutoGridSocial automates posting for {post.related_landing_label.toLowerCase()} — try it — $29/mo or a $10 one-time pack.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="bg-primary hover:bg-primary/90 text-white h-11 px-6 group" data-testid="blog-related-landing-link">
              <Link to={`/for/${post.related_landing_slug}`}>
                See the {post.related_landing_label} page
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button variant="outline" onClick={handleLogin} className="h-11 px-6 border-primary/40 text-primary" data-testid="blog-cta-trial">
              Get started
            </Button>
          </div>
        </div>

        {/* FAQ */}
        {post.faq_items?.length > 0 && (
          <section className="mt-4">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">FAQ</h2>
            <div className="space-y-4">
              {post.faq_items.map((f, i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-5" data-testid={`blog-faq-${i}`}>
                  <h3 className="font-semibold text-sm mb-2 flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </article>

      <SiteFooter />
      <ExitIntentOffer />
    </div>
  );
}
